import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function AdminAnalytics({ token }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');

  // Fetch timezone setting
  useEffect(() => {
    fetch(`${API_URL}/api/settings/timezone`)
      .then(r => r.json())
      .then(data => { if (data.value) setTimezone(data.value); })
      .catch(() => {});
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch analytics');
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) return <div className="admin-analytics"><p>Loading analytics...</p></div>;
  if (error) return <div className="admin-analytics"><p className="error">{error}</p></div>;
  if (!analytics) return null;

  const totalBookings = analytics.byStatus.reduce((sum, s) => sum + parseInt(s.count), 0);
  const approvedCount = analytics.byStatus.find(s => s.status === 'approved')?.count || 0;
  const pendingCount = analytics.byStatus.find(s => s.status === 'pending')?.count || 0;
  const rejectedCount = analytics.byStatus.find(s => s.status === 'rejected')?.count || 0;
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // Format month for display
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const exportToCSV = () => {
    const lines = [];
    
    // Summary
    lines.push('ANALYTICS SUMMARY');
    lines.push(`Export Date,${new Date().toLocaleDateString()}`);
    lines.push('');
    lines.push('BOOKING TOTALS');
    lines.push('Status,Count');
    lines.push(`Total Bookings,${totalBookings}`);
    lines.push(`Approved,${approvedCount}`);
    lines.push(`Pending,${pendingCount}`);
    lines.push(`Rejected,${rejectedCount}`);
    lines.push('');
    
    // By Type
    lines.push('BOOKINGS BY TYPE');
    lines.push('Type,Count');
    analytics.byType.forEach(item => {
      lines.push(`${item.booking_type.replace('_', ' ')},${item.count}`);
    });
    lines.push('');
    
    // By Day of Week
    lines.push('BOOKINGS BY DAY OF WEEK');
    lines.push('Day,Count');
    analytics.byDayOfWeek.forEach(item => {
      lines.push(`${DAYS[parseInt(item.day)]},${item.count}`);
    });
    lines.push('');
    
    // By Hour
    lines.push('BOOKINGS BY HOUR');
    lines.push('Hour,Count');
    analytics.byHour.forEach(item => {
      lines.push(`${item.hour}:00,${item.count}`);
    });
    lines.push('');
    
    // Top Organizations
    lines.push('TOP ORGANIZATIONS');
    lines.push('Organization,Bookings');
    analytics.byOrg.forEach(org => {
      // Escape commas in organization names
      const orgName = org.organization.includes(',') ? `"${org.organization}"` : org.organization;
      lines.push(`${orgName},${org.count}`);
    });
    lines.push('');
    
    // Recent Activity
    lines.push('LAST 30 DAYS ACTIVITY');
    lines.push('Date,Total,Approved,Rejected,Pending');
    analytics.recentActivity.forEach(day => {
      const total = parseInt(day.requests);
      const approved = parseInt(day.approved);
      const rejected = parseInt(day.rejected);
      const pending = total - approved - rejected;
      lines.push(`${day.date},${total},${approved},${rejected},${pending}`);
    });
    lines.push('');
    
    // Revenue by Month
    lines.push('REVENUE BY MONTH');
    lines.push('Month,Revenue');
    (analytics.revenueByMonth || []).forEach(item => {
      lines.push(`${formatMonth(item.month)},${parseFloat(item.revenue).toFixed(2)}`);
    });
    lines.push('');
    
    // Revenue by Type
    lines.push('REVENUE BY RENTAL TYPE');
    lines.push('Type,Revenue');
    (analytics.revenueByType || []).forEach(item => {
      const typeLabel = item.booking_type === 'full_turf' ? 'Full Turf' 
        : item.booking_type === 'half_turf' ? 'Half Turf'
        : item.booking_type === 'cage_rental' ? 'Cage Rental'
        : item.booking_type;
      lines.push(`${typeLabel},${parseFloat(item.revenue).toFixed(2)}`);
    });
    lines.push('');
    
    // Customer Stats
    lines.push('CUSTOMER STATISTICS');
    lines.push('Metric,Count');
    lines.push(`Total Customers,${analytics.customerCount?.total_customers || 0}`);
    lines.push(`Customers With Bookings,${analytics.customerCount?.with_bookings || 0})`);
    lines.push('');
    
    // Sales Summary
    lines.push('SALES SUMMARY (YTD)');
    lines.push('Metric,Amount');
    lines.push(`Total Revenue (Invoiced),${(analytics.totalRevenue || 0).toFixed(2)}`);
    lines.push(`Potential Sales (Not Invoiced),${(analytics.potentialSales || 0).toFixed(2)}`);
    
    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-analytics">
      <div className="analytics-header">
        <h2>Analytics Dashboard</h2>
        <button className="export-btn" onClick={exportToCSV}>
          Export CSV
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card total">
          <span className="stat-value">{totalBookings}</span>
          <span className="stat-label">Total Bookings</span>
        </div>
        <div className="stat-card approved">
          <span className="stat-value">{approvedCount}</span>
          <span className="stat-label">Approved</span>
        </div>
        <div className="stat-card pending">
          <span className="stat-value">{pendingCount}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-card rejected">
          <span className="stat-value">{rejectedCount}</span>
          <span className="stat-label">Rejected</span>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card revenue">
          <span className="stat-value">{formatCurrency(analytics.totalRevenue || 0)}</span>
          <span className="stat-label">Total Revenue</span>
          <span className="stat-subtitle">Invoiced YTD</span>
        </div>
        <div className="stat-card potential-sales">
          <span className="stat-value">{formatCurrency(analytics.potentialSales || 0)}</span>
          <span className="stat-label">Potential Sales</span>
          <span className="stat-subtitle">Not yet invoiced</span>
        </div>
        <div className="stat-card customers">
          <span className="stat-value">{analytics.customerCount?.total_customers || 0}</span>
          <span className="stat-label">Total Customers</span>
        </div>
        <div className="stat-card active-customers">
          <span className="stat-value">{analytics.customerCount?.with_bookings || 0}</span>
          <span className="stat-label">With Bookings</span>
        </div>
      </div>

      <div className="analytics-sections">
        <div className="analytics-section">
          <h3>Bookings by Type</h3>
          <div className="bar-chart">
            {analytics.byType.map(item => {
              const maxCount = Math.max(...analytics.byType.map(t => parseInt(t.count)));
              const width = maxCount > 0 ? (parseInt(item.count) / maxCount) * 100 : 0;
              return (
                <div key={item.booking_type} className="bar-row">
                  <span className="bar-label">{item.booking_type.replace('_', ' ')}</span>
                  <div className="bar-container">
                    <div className="bar" style={{ width: `${width}%` }}></div>
                    <span className="bar-value">{item.count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="analytics-section">
          <h3>Bookings by Day of Week</h3>
          <div className="bar-chart">
            {analytics.byDayOfWeek.map(item => {
              const maxCount = Math.max(...analytics.byDayOfWeek.map(d => parseInt(d.count)));
              const width = maxCount > 0 ? (parseInt(item.count) / maxCount) * 100 : 0;
              return (
                <div key={item.day} className="bar-row">
                  <span className="bar-label">{DAYS[parseInt(item.day)]}</span>
                  <div className="bar-container">
                    <div className="bar" style={{ width: `${width}%` }}></div>
                    <span className="bar-value">{item.count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="analytics-section">
          <h3>Peak Hours</h3>
          <div className="bar-chart horizontal-scroll">
            {analytics.byHour.map(item => {
              const maxCount = Math.max(...analytics.byHour.map(h => parseInt(h.count)));
              const height = maxCount > 0 ? (parseInt(item.count) / maxCount) * 100 : 0;
              return (
                <div key={item.hour} className="hour-bar">
                  <div className="hour-bar-fill" style={{ height: `${height}%` }}></div>
                  <span className="hour-label">{item.hour}:00</span>
                  <span className="hour-value">{item.count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="analytics-section">
          <h3>Top Organizations</h3>
          {analytics.byOrg.length === 0 ? (
            <p className="no-data">No organization data</p>
          ) : (
            <table className="org-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Bookings</th>
                </tr>
              </thead>
              <tbody>
                {analytics.byOrg.map((org, i) => (
                  <tr key={i}>
                    <td>{org.organization}</td>
                    <td>{org.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="analytics-section full-width">
        <h3>Last 30 Days Activity</h3>
        {analytics.recentActivity.length === 0 ? (
          <p className="no-data">No recent activity</p>
        ) : (
          <div className="activity-chart">
            {analytics.recentActivity.slice(-14).map(day => {
              const total = parseInt(day.requests);
              const approved = parseInt(day.approved);
              const rejected = parseInt(day.rejected);
              const maxHeight = Math.max(...analytics.recentActivity.map(d => parseInt(d.requests)));
              const height = maxHeight > 0 ? (total / maxHeight) * 80 : 0;
              // Handle both ISO date strings and YYYY-MM-DD formats
              const dateStr = typeof day.date === 'string' ? day.date.slice(0, 10) : new Date(day.date).toISOString().slice(0, 10);
              const [year, month, dayNum] = dateStr.split('-').map(Number);
              const dateObj = new Date(year, month - 1, dayNum);
              return (
                <div key={day.date} className="activity-bar">
                  <div className="activity-stack" style={{ height: `${height}px` }}>
                    {approved > 0 && <div className="approved-part" style={{ flex: approved }}></div>}
                    {rejected > 0 && <div className="rejected-part" style={{ flex: rejected }}></div>}
                    {(total - approved - rejected) > 0 && <div className="pending-part" style={{ flex: total - approved - rejected }}></div>}
                  </div>
                  <span className="date-label">{dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="analytics-sections">
        <div className="analytics-section">
          <h3>Revenue by Month</h3>
          {(!analytics.revenueByMonth || analytics.revenueByMonth.length === 0) ? (
            <p className="no-data">No revenue data</p>
          ) : (
            <div className="bar-chart">
              {analytics.revenueByMonth.slice().reverse().map(item => {
                const maxRevenue = Math.max(...analytics.revenueByMonth.map(m => parseFloat(m.revenue || 0)));
                const width = maxRevenue > 0 ? (parseFloat(item.revenue) / maxRevenue) * 100 : 0;
                return (
                  <div key={item.month} className="bar-row">
                    <span className="bar-label">{formatMonth(item.month)}</span>
                    <div className="bar-container">
                      <div className="bar revenue-bar" style={{ width: `${width}%` }}></div>
                      <span className="bar-value">{formatCurrency(item.revenue)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="analytics-section">
          <h3>Revenue by Rental Type</h3>
          {(!analytics.revenueByType || analytics.revenueByType.length === 0) ? (
            <p className="no-data">No revenue data</p>
          ) : (
            <div className="bar-chart">
              {analytics.revenueByType.map(item => {
                const maxRevenue = Math.max(...analytics.revenueByType.map(t => parseFloat(t.revenue || 0)));
                const width = maxRevenue > 0 ? (parseFloat(item.revenue) / maxRevenue) * 100 : 0;
                const typeLabel = item.booking_type === 'full_turf' ? 'Full Turf' 
                  : item.booking_type === 'half_turf' ? 'Half Turf'
                  : item.booking_type === 'cage_rental' ? 'Cage Rental'
                  : item.booking_type;
                return (
                  <div key={item.booking_type} className="bar-row">
                    <span className="bar-label">{typeLabel}</span>
                    <div className="bar-container">
                      <div className="bar revenue-bar" style={{ width: `${width}%` }}></div>
                      <span className="bar-value">{formatCurrency(item.revenue)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminAnalytics;
